import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { TrendingUp, MapPin } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Real-only social proof. Renders nothing if the DB has no qualifying
 * city (>= 5 actual paid Pulse purchases in the last 90 days). We never
 * fabricate counters — silent absence is the correct UX while the
 * product is still gaining traction.
 */
export const PulseSocialProof = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let mounted = true;
    axios.get(`${API}/pulse/social-proof`)
      .then((res) => { if (mounted) setData(res.data); })
      .catch(() => { if (mounted) setError(true); });
    return () => { mounted = false; };
  }, []);

  if (error || !data) return null;
  if (!data.cities?.length) {
    // Real-only: no cities yet over the threshold — render nothing.
    return null;
  }

  return (
    <div
      data-testid="pulse-social-proof"
      className="mt-10 rounded-2xl border border-emerald-200 bg-emerald-50/40 p-6"
    >
      <div className="flex items-center gap-2 mb-4">
        <TrendingUp className="h-5 w-5 text-emerald-600" />
        <p className="text-[10px] tracking-[0.22em] uppercase font-bold text-emerald-700">
          Real subscribers · last 90 days
        </p>
      </div>
      <p
        data-testid="pulse-social-proof-headline"
        className="text-2xl sm:text-3xl font-['Playfair_Display'] text-[#0F172A] mb-1"
      >
        {data.total_paid.toLocaleString()} buyers across {data.cities.length}{' '}
        {data.cities.length === 1 ? 'city' : 'cities'} get Pulse Top 10 every Monday.
      </p>
      <p className="text-sm text-slate-600 mb-4">
        Each number below is an actual paid Pulse subscriber — no estimates, no placeholders.
      </p>

      <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
        {data.cities.map((c) => (
          <li
            key={c.city}
            data-testid={`pulse-social-proof-city-${c.city.toLowerCase()}`}
            className="flex items-center justify-between rounded-lg bg-white border border-emerald-100 px-3 py-2"
          >
            <span className="text-sm text-[#0F172A] flex items-center gap-1.5">
              <MapPin className="h-3.5 w-3.5 text-emerald-500" />
              {c.city}
            </span>
            <span className="text-sm font-bold text-emerald-700">{c.count}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default PulseSocialProof;
